import { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ConfigContext } from '../shared/ConfigContext';
import OrderClient from '../../api/orderClient';
import { quickEta, getRejectPresets } from './orderActionHelpers';

const PAGE_SIZE = 25;

// Same accumulating "load more" fetch pattern as the other Step 4/5 pages.
// Client Orders additionally owns the accept/reject/payment dialogs, which
// call OrderClient directly (same endpoints, payloads and guards as before —
// CHANGES.md 5.2/5.4 only changes which button is visible when).
const useClientOrdersPage = () => {
  const { t } = useTranslation();
  const config = useContext(ConfigContext);
  const [searchParams] = useSearchParams();
  const previewState = searchParams.get('previewState');

  const [companyId] = useState(() => localStorage.getItem('companyId') || '');
  const orderClient = useMemo(() => (config?.backendUrl ? new OrderClient(config.backendUrl) : null), [config]);

  const [rows, setRows] = useState([]);
  const rowsRef = useRef(rows);
  useEffect(() => { rowsRef.current = rows; }, [rows]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('loading');
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorInfo, setErrorInfo] = useState(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [detailOrder, setDetailOrder] = useState(null);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const [acceptOpen, setAcceptOpen] = useState(false);
  const [acceptTarget, setAcceptTarget] = useState(null);
  const [acceptEta, setAcceptEta] = useState('');
  const [accepting, setAccepting] = useState(false);

  const [paymentDialog, setPaymentDialog] = useState({ open: false, order: null, targetPaid: null });
  const [paymentPendingId, setPaymentPendingId] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const fetchOrders = useCallback((reset = true) => {
    if (!config?.backendUrl || !companyId) return;
    if (reset) setStatus('loading'); else setLoadingMore(true);
    const page = reset ? 1 : Math.floor(rowsRef.current.length / PAGE_SIZE) + 1;
    const params = { companyId, page, pageSize: PAGE_SIZE, sort: sortBy };
    if (statusFilter !== 'all') params.status = statusFilter;
    if (search) params.search = search;
    axios.get(`${config.backendUrl}/orders`, { params })
      .then((res) => {
        setRows((prev) => (reset ? res.data.items : [...prev, ...res.data.items]));
        setTotal(res.data.total);
        setStatus('ready');
      })
      .catch((error) => {
        console.error('Error fetching orders', error);
        if (error.response?.status === 403) {
          setStatus('permission');
        } else {
          setErrorInfo({
            request: `GET /orders · ${error.response?.status || t('N/A')} · ${new Date().toLocaleTimeString()}`,
          });
          setStatus('error');
        }
      })
      .finally(() => setLoadingMore(false));
  }, [config, companyId, search, statusFilter, sortBy, t]);

  useEffect(() => { fetchOrders(true); }, [fetchOrders]);

  const loadMore = () => fetchOrders(false);
  const remaining = Math.max(0, total - rows.length);
  const nextBatchSize = Math.min(PAGE_SIZE, remaining);

  // ---- Accept (requires ETA) -------------------------------------------//
  const openAcceptDialog = (order) => {
    setAcceptTarget(order);
    setAcceptEta('');
    setAcceptOpen(true);
  };
  const applyQuickEta = (kind) => setAcceptEta(quickEta(kind));

  const handleAccept = async () => {
    if (!acceptEta || !orderClient) return;
    setAccepting(true);
    try {
      await orderClient.acceptOrder(acceptTarget._id);
      try {
        await orderClient.setOrderEta(acceptTarget._id, acceptEta);
        notify(t('Order accepted and ETA updated successfully!'));
      } catch (etaError) {
        console.error('Error updating ETA:', etaError);
        notify(t('Order accepted but error updating ETA.'), 'warning');
      }
      setAcceptOpen(false);
      fetchOrders();
    } catch (error) {
      console.error('Error accepting order:', error);
      notify(t('Error accepting order.'), 'error');
    } finally {
      setAccepting(false);
    }
  };

  // ---- Reject (requires a reason) --------------------------------------//
  const rejectPresets = getRejectPresets(t);

  const openRejectDialog = (order) => {
    setRejectTarget(order);
    setRejectReason('');
    setRejectOpen(true);
  };

  const handleReject = async () => {
    if (!rejectReason.trim() || !orderClient) return;
    setRejecting(true);
    try {
      await orderClient.rejectOrder(rejectTarget._id, rejectReason);
      notify(t('Order rejected successfully.'));
      setRejectOpen(false);
      fetchOrders();
    } catch (error) {
      console.error('Error rejecting order:', error);
      notify(t('Error rejecting order.'), 'error');
    } finally {
      setRejecting(false);
    }
  };

  // ---- Payment (mark paid / unpaid) — invoice-managed orders never reach
  // this; the row shows a disabled note instead (CHANGES.md 5.4). ---------//
  const openPaymentDialog = (order, targetPaid) => setPaymentDialog({ open: true, order, targetPaid });
  const closePaymentDialog = () => setPaymentDialog({ open: false, order: null, targetPaid: null });

  const handlePaymentError = (error) => {
    const status_ = error.response?.status;
    const backendMessage = error.response?.data?.error;
    if (status_ === 409) {
      fetchOrders();
      notify(backendMessage || t('Payment status has changed. The order was refreshed.'), 'warning');
    } else if (status_ === 404) {
      fetchOrders();
      notify(backendMessage || t('Order no longer exists.'), 'error');
    } else {
      notify(backendMessage || t('Payment could not be updated. Please try again.'), 'error');
    }
  };

  const handleConfirmPayment = async () => {
    const { order, targetPaid } = paymentDialog;
    if (!order || !orderClient) return;
    setPaymentPendingId(order._id);
    try {
      await orderClient.setOrderPayment(order._id, targetPaid);
      notify(t(targetPaid ? 'Order marked as paid.' : 'Order marked as unpaid.'));
      closePaymentDialog();
      fetchOrders();
    } catch (error) {
      console.error('Error updating order payment:', error);
      handlePaymentError(error);
      closePaymentDialog();
    } finally {
      setPaymentPendingId(null);
    }
  };

  const isPreviewEmptyKind = previewState === 'empty' || previewState === 'no-match';
  const effectiveStatus = isPreviewEmptyKind ? 'ready' : (previewState || status);
  const effectiveRows = isPreviewEmptyKind ? [] : (effectiveStatus === 'ready' ? rows : []);
  const isNoMatch = isPreviewEmptyKind ? previewState === 'no-match' : Boolean(search || statusFilter !== 'all');

  return {
    t, total, errorInfo, effectiveStatus, effectiveRows, isNoMatch, search,
    searchInput, setSearchInput, setSearch, statusFilter, setStatusFilter, sortBy, setSortBy,
    detailOrder, setDetailOrder,
    loadingMore, loadMore, remaining, nextBatchSize,
    rejectOpen, setRejectOpen, rejectTarget, rejectReason, setRejectReason, rejecting, rejectPresets, openRejectDialog, handleReject,
    acceptOpen, setAcceptOpen, acceptTarget, acceptEta, setAcceptEta, accepting, applyQuickEta, openAcceptDialog, handleAccept,
    paymentDialog, openPaymentDialog, closePaymentDialog, paymentPendingId, handleConfirmPayment,
    snackbar, setSnackbar, fetchOrders,
  };
};

export default useClientOrdersPage;
